import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Autocomplete } from '@material-ui/lab';
import NumberFormat from 'react-number-format';
import {
  Button,
  TextField,
  Card,
  Select,
  MenuItem,
  Grid,
  Checkbox,
} from '@material-ui/core';
import {
  getQuoteProGroupsAttributeApi,
  addTasksToQueueApi
} from 'api';
import { imgBaseUrl } from 'configs';
import MUIDataTable from 'mui-datatables';
import {
  Breadcrumb
} from 'components';
import logo from 'components/Logo/logo-dermpro.svg';
import useStyles from './QuoteProduct/styles';


const BulkEditProductsGroups = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const [editedProducts, setEditedProducts] = useState([]);
  const [skusToSave, setSkusToSave] = useState([]);

  const { stores = [] } = useSelector((state: IReduxState) => state.user.profile);
  const {profile} = useSelector((state: IReduxState) => state.user);
  const permissions = profile?.config?.permissions || {};
  const { massEditList } = useSelector((state: IReduxState) => state.quote);

  const {quoteGroupsAttributeData} = useSelector((state: IReduxState) => state.quote);
  const groups = quoteGroupsAttributeData?.options.filter((group) => !!group.value.length) || [];

  const mediaStoreUrl = `${stores[0]?.apiUrl}/media/catalog/product` || imgBaseUrl;
  const userStore = stores?.[0];

  if (!massEditList.length) {
    history.push('/quoting/listings/active');
  }

  useEffect(() => {
    if (!quoteGroupsAttributeData.options?.length) {
      getQuoteProGroupsAttributeApi({dispatch});
    }
  }, []);

  const handleChangePrice = (val, product, minProductPrice) => {
    if (!minProductPrice || val.floatValue > minProductPrice) {
      const alreadyEdited = editedProducts.find((p) => p.sku === product.sku);

      if (alreadyEdited) {
        setEditedProducts((editedProducts) => editedProducts.map((p) => {
          if (p.sku === product.sku) {
            return {...p, price: val.floatValue};
          }

          return p;
        }));
      } else {
        setEditedProducts([...editedProducts, { id: product.id, sku: product.sku, price: val.floatValue }]);
      }
    }

    setSkusToSave((skusToSave) => [...new Set([...skusToSave, product.sku])]);
  };

  const handleChangeGroups = (product, values) => {
    const alreadyEdited = editedProducts.find((edited) => edited.sku === product.sku);

    const groupIds = values.map((g) => g.value).join(',');
    const groupsAttribute = {
      attribute_code: 'catalog_quotepro_groups',
      value: groupIds
    };

    if (alreadyEdited) {
      setEditedProducts((editedProducts) => editedProducts.map((pr) => {
        if (pr.sku === product.sku) {
          return {...pr, custom_attributes: [groupsAttribute]};
        }

        return pr;
      }));
    } else {
      setEditedProducts([...editedProducts, { id: product.id, sku: product.sku, custom_attributes: [groupsAttribute]}]);
    }

    setSkusToSave((skusToSave) => [...new Set([...skusToSave, product.sku])]);
  };

  const handleChangeStatus = (product, status) => {
    const alreadyEdited = editedProducts.find((edited) => edited.sku === product.sku);

    if (alreadyEdited) {
      setEditedProducts((editedProducts) => editedProducts.map((pr) => {
        if (pr.sku === product.sku) {
          return {...pr, status};
        }

        return pr;
      }));
    } else {
      setEditedProducts([...editedProducts, { id: product.id, sku: product.sku, status}]);
    }

    setSkusToSave((skusToSave) => [...new Set([...skusToSave, product.sku])]);
  };

  const handleClickSave = async (product) => {
    let editedProduct = editedProducts.find((p) => p.sku === product.sku);
    const productImgAttr = product.custom_attributes?.find((attr) => attr.attribute_code === 'image');
    const editedAttrWithoutImg = editedProduct?.custom_attributes?.filter((a) => a.attribute_code !== 'image') || [];

    editedProduct = {...editedProduct, custom_attributes: [...editedAttrWithoutImg, productImgAttr]};

    const task = {
      sku: editedProduct.sku,
      name: product.name,
      body: editedProduct,
      requestType: 'UPDATE_PRODUCT',
      storeViewCode: userStore.quoteProStoreCode
    };


    await addTasksToQueueApi({ dispatch, tasks: [task] });
    setSkusToSave((skusToSave) => skusToSave.filter((sku) => sku !== product.sku));
  };

  const handleSaveAll = async () => {
    const tasks = editedProducts.map((editedProduct) => {
      const product = massEditList.find((p) => p.sku === editedProduct.sku);
      const productImgAttr = product.custom_attributes?.find((attr) => attr?.attribute_code === 'image');
      const editedAttrWithoutImg = editedProduct?.custom_attributes?.filter((a) => a.attribute_code !== 'image') || [];
      const customAttr = productImgAttr ? [...editedAttrWithoutImg, productImgAttr] : editedAttrWithoutImg;

      editedProduct = {...editedProduct, custom_attributes: customAttr};

      return {
        sku: editedProduct.sku,
        name: product.name,
        body: editedProduct,
        requestType: 'UPDATE_PRODUCT',
        storeViewCode: userStore.quoteProStoreCode
      };
    });

    await addTasksToQueueApi({ dispatch, tasks });

    history.push('/quoting/listings/active');
  };

  const columns = [
    {
      name: 'custom_attributes',
      label: 'Item',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const product = massEditList[dataIndex];
          const {custom_attributes = []} = product;
          const productImagePath = custom_attributes?.find((attr) => attr?.attribute_code === 'image')?.value;
          const image = productImagePath ? `${mediaStoreUrl}${productImagePath}` : logo;

          return (
            <div className="flex items-center">
              <img
                src={image}
                alt={product.name}
                style={{maxHeight: '60px', width: '60px', objectFit: 'contain'}}
                className="border-radius-8"
              />
              <div className="ml-3">
                <p className="my-0 text-15" >{product.name}</p>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'custom_attributes',
      label: 'Groups',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const product = massEditList[dataIndex];
          let currentGroupsIds = [];
          const alreadyEdited = editedProducts.find((edited) => edited.sku === product.sku);

          if (alreadyEdited?.custom_attributes) {
            currentGroupsIds = alreadyEdited.custom_attributes.find((attr) => attr.attribute_code === 'catalog_quotepro_groups')?.value?.split(',') || [];
          } else {
            currentGroupsIds = product?.custom_attributes.find((attr) => attr.attribute_code === 'catalog_quotepro_groups')?.value?.split(',') || [];
          }

          return (
            <div className="w-full">
              <Autocomplete
                multiple
                className={classes.autocomplete}
                options={groups}
                disableCloseOnSelect
                disableClearable
                value={groups.filter((group) => currentGroupsIds.includes(group.value))}
                getOptionLabel={(option) => option.label}
                renderOption={(option, { selected }) => (
                  <>
                    <Checkbox
                      color="primary"
                      checked={selected}
                    />
                    {option.label}
                  </>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Add Group"
                  />
                )}
                ChipProps={{
                  className: classes.chip
                }}
                onChange={(event, values) => handleChangeGroups(product, values)}
              />
            </div>
          );
        },
      },
    },
    {
      name: 'price',
      label: 'Price',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ className: 'w-120 ml-5 p-0' }),
        setCellProps: () => ({ className: 'p-0' }),
        customBodyRenderLite: (dataIndex) => {
          const product = massEditList[dataIndex];
          const { sku, price: productPrice, custom_attributes } = product;
          const editedProduct = editedProducts.find((edited) => edited.sku === sku);
          const price = editedProduct?.price || productPrice;
          const minProductPrice = Number(custom_attributes?.find((item) => item.attribute_code === 'catalog_min_price')?.value);
          const lessThanMinPrice = Boolean(minProductPrice && price < minProductPrice);

          return (
            <NumberFormat
              key={product.sku}
              allowNegative={false}
              prefix="$"
              fixedDecimalScale
              decimalScale={2}
              thousandSeparator
              name="price"
              defaultValue={price}
              className="mx-0 p-0"
              inputProps={{className: 'text-center min-w-60' }}
              onValueChange={(val) => handleChangePrice(val, product, minProductPrice)}
              customInput={TextField}
              error={lessThanMinPrice}
              helperText={lessThanMinPrice && `MAP cannot go lower than $${minProductPrice}`}
            />
          );
        },
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ style: { width: '100px' }, className: 'text-right pr-8 text-14' }),
        setCellProps: () => ({ className: 'text-right' }),
        customBodyRenderLite: (dataIndex) => {
          const product = massEditList[dataIndex];
          let status = product.status == 1 ? '1' : '2';
          const alreadyEdited = editedProducts.find((edited) => edited.sku === product.sku);

          if (alreadyEdited?.status) {
            ({status} = alreadyEdited);
          }

          return (
            <Select
              key={product.sku}
              name="status"
              value={status}
              label="Status"
              onChange={(event) => handleChangeStatus(product, event.target.value)}
            >
              <MenuItem value="1">Enabled</MenuItem>
              <MenuItem value="2">Disabled</MenuItem>
            </Select>
          );
        },
      },
    },
    {
      name: 'action',
      label: 'Action',
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ style: { width: '100px' }, className: 'text-right pr-8 text-14' }),
        setCellProps: () => ({ className: 'text-right' }),
        customBodyRenderLite: (dataIndex) => {
          const product = massEditList[dataIndex];
          const editedProduct = editedProducts.find((edited) => edited.sku === product.sku);

          return (
            <Button
              variant="contained"
              color="primary"
              title="Save"
              disabled={!skusToSave.includes(editedProduct?.sku)}
              onClick={() => handleClickSave(product)}
            >
              Save
            </Button>
          );
        },
      },
    },
  ];

  return (
    <div className="flex-grow h-full scroll-y pb-25">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Quote Listings', path: '/quoting/listings/active' },
            { name: 'Bulk Edit' }
          ]}
        />
      </div>
      <Grid container xs={12} className="flex px-8 pt-0 pb-4" justifyContent="flex-end">
        {permissions.massEditSaveAll &&
        <Button
          type="button"
          color="primary"
          variant="contained"
          className="uppercase custom-check-all custom-check-all-select"
          title="Click here to save all products"
          onClick={handleSaveAll}
          disabled={!skusToSave?.length}
        >
          Save All & go back
        </Button>}
      </Grid>
      <div className="mx-10">
        {massEditList?.length > 0 &&
        <Card>
          <MUIDataTable
            data={massEditList}
            columns={columns}
            options={{
              filterType: 'textField',
              responsive: 'standard',
              selectableRows: 'none',
              viewColumns: false,
              search: false,
              filter: false,
              print: false,
              download: false,
              enableNestedDataAccess: '.',
              rowsPerPageOptions: [10, 20, 40, 80, 100]
            }}
          />
        </Card>
        }
      </div>
    </div>
  );
};

export default BulkEditProductsGroups;
